/*
 * Copyright (c) Vaisala Oyj. All rights reserved.
 */

import * as React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { setConfiguration } from 'react-grid-system';
import { useTranslation } from 'react-i18next';
import 'reflect-metadata';
import { ApplicationTheme, Application as VaiApplication } from '@vaisala-rockhopper/components';
import LanguageController from '../modules/LanguageController';
import { Fallback } from './Fallback';
import Header from './a-parameter-calculator/custom/navigation/Header';
import { ModalProps, Modals, ModalType } from './a-parameter-calculator/custom/modals/Modals';
import { MobileMenu } from './a-parameter-calculator/custom/navigation/MobileMenu';
import LandingPage from '../pages/a-parameter-calculator/LandingPage';
import { OptionsConfig } from './a-parameter-calculator/custom/modals/OptionsModal';
import UnitController from '../modules/units/UnitController';
import Analytics from '../modules/analytics/Analytics';
import PersistenceController from '../modules/persistence/PersistenceController';
import PwaController from '../modules/pwa/PwaController';
import { useServiceWorker } from './chemical-curve/context/ServiceWorkerContext';
import { ScrollNavContext } from './chemical-curve/context/ScrollNavContext';
import paramConfig from '../modules/calculator/a-parameter-calculator/parameterConfig_NEW.json';
import SplashScreen from './a-parameter-calculator/custom/utils/SplashScreen';
import { isMobileOnly } from 'react-device-detect';
import ChemicalDataController, { CurveData } from '../modules/ChemicalDataController';
import { useSearchParams, useNavigate } from 'react-router-dom';
import pkceChallenge from 'pkce-challenge'

interface Method {
  id: number;
  heading: string;
  subHeading: string;
  longDesc: string;
}

// Setup layout grid
setConfiguration({ defaultScreenClass: 'sm', containerWidths: [1100, 1100, 1100, 1100, 1100], gutterWidth: 16 });

export const AParameterRoot = () => {
  const { t, i18n } = useTranslation(['a-parameter-calculator']);
  Object.assign(LanguageController, { t, i18n });

  ////////////////////////////////////////////////////////////
  // MENUS
  ////////////////////////////////////////////////////////////

  const [theme, setTheme] = React.useState(ApplicationTheme.Light);
  const toggleTheme = () => changeTheme(theme === ApplicationTheme.Light ? ApplicationTheme.Dark : ApplicationTheme.Light);
  const chooseTheme = (ind: number) => {
    changeTheme(ind.toString() === '0' ? ApplicationTheme.Light : ApplicationTheme.Dark);
  };

  const changeTheme = (t: ApplicationTheme) => {
    PersistenceController.store('theme', t);
    setTheme(t);

    // TODO: Actually fix this
    // fix Rockhopper bug (theme css class is not set properly, leaving modals always in light mode)
    document.body.classList.add(t);

    Analytics.log('settings.theme', t === ApplicationTheme.Light ? 'light' : 'dark');
  };

  const [unit, setUnit] = React.useState(0);
  const chooseUnit = (ind: number) => {
    UnitController.currentModeIndex = ind;
    PersistenceController.store('unitIndex', ind);
    setUnit(ind);
  };

  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const onMobileMenuOpen = () => {
    setMobileMenuOpen(true);
    Analytics.log('navigation.mobilemenu', 'open');
  };
  const onMobileMenuClose = () => {
    setMobileMenuOpen(false);
    Analytics.log('navigation.mobilemenu', 'close');
  };

  ////////////////////////////////////////////////////////////
  // MODALS
  ////////////////////////////////////////////////////////////

  const [modal, setModal] = React.useState<ModalProps>();
  const modalId = (s: string) => s.split('.')[1];
  const openModal = (type: ModalType, title?: string, optionsConfig?: OptionsConfig, onAccept?: VoidFunction, onReject?: VoidFunction) => {
    onMobileMenuClose();
    setModal({
      currentModal: type,
      onCloseModal: onCloseModal,
      title: title,
      optionsConfig: optionsConfig,
      theme: theme,
      activeMethod: activeMethod,
      onAccept: onAccept,
      onReject: onReject,
    });
    Analytics.log('navigation.modal', 'open', modalId(type));
  };

  const onCloseModal = () => {
    Analytics.log('navigation.modal', 'close');
    setModal(undefined);
  };

  const onHelpOpen = () => {
    openModal('modal.help');
  };

  const onOptionsOpen = (title: string, config: OptionsConfig) => {
    openModal('modal.options', title, {
      options: config.options,
      selectedIndex: config.selectedIndex,
      onSelect: (i: number) => {
        config.onSelect(i);
        onCloseModal();
      },
    });
  };

  const onShowPWAPrompt = () => {
    openModal('modal.pwa', '', undefined, onPWAAccept, onPWAReject);
  };

  ////////////////////////////////////////////////////////////
  // PWA
  ////////////////////////////////////////////////////////////

  const [showPWAButton, setShowPWAButton] = React.useState(false);

  const onPWAAvailable = () => {
    Analytics.log('navigation.pwa_offer', 'available');
    setShowPWAButton(true);
  };

  const onPWAProcessed = () => {
    setShowPWAButton(false);
  };

  const onPWAAccept = () => {
    Analytics.log('navigation.pwa_offer', 'accept');
    onCloseModal();
    PwaController.onShowPWAPrompt();
  };

  const onPWAReject = () => {
    Analytics.log('navigation.pwa_offer', 'reject');
    onCloseModal();
  };

  ////////////////////////////////////////////////////////////
  // Active Chemical and Curve Data
  ////////////////////////////////////////////////////////////

  const [activeMethod, setActiveMethod] = React.useState<number>(-1);
  const [method, setMethod] = React.useState<Method>({ id: -1, heading: '', subHeading: '', longDesc: '' });
  const [, setActivePhase] = React.useContext(ScrollNavContext);
  const [selectedChemical, setSelectedChemical] = React.useState<number>(-1);
  const [recentChemicals, setRecentChemicals] = React.useState<CurveData[]>([])

  const [allChemicalCurves, setAllChemicalCurves] = React.useState<CurveData[]>([])

  React.useEffect(() => {
      const recent = PersistenceController.retrieve('recent')
      if (recent) {
        setRecentChemicals(recent)
      }
      async function getData() {
          const data = await ChemicalDataController.getAllChemicalCurves();
          setAllChemicalCurves(data);
      }
      getData();
  }, []);

  const onMethodSelect = React.useCallback(
    (method: number) => {
      const selectedMethod = paramConfig.methods.find((x: Method) => x.id === method);
      const mobileCalculator = document.getElementById('mobile-calculator');
      const desktopCalculator = document.getElementById('desktop-calculator');
      const desktopGraph = document.getElementById('desktop-graph');
      if (method > -1) {
        setActivePhase(1);
        if (mobileCalculator) {
          mobileCalculator.scrollTop = 0;
        }
        if (desktopCalculator) {
          desktopCalculator.scrollTop = 0;
        }
        if (desktopGraph) {
          desktopGraph.scrollTop = 0;
        }
      }
      if (selectedMethod) {
        setActiveMethod(method);
        setMethod(selectedMethod);
        const analyticsLabel = method === 0 ? 'closed_system' : method === 1 ? 'open_system' : method === 2 ? 'free_evaporation' : null;
        if (analyticsLabel) {
          Analytics.log(`navigation.${analyticsLabel}`);
        }
      } else {
        setActiveMethod(method);
        Analytics.log(`navigation.landing_page`);
        setTimeout(() => {
          setMethod({ id: -1, heading: '', subHeading: '', longDesc: '' });
        }, 350);
      }
    },
    [setActivePhase]
  );

  ////////////////////////////////////////////////////////////
  // INIT
  ////////////////////////////////////////////////////////////

  React.useEffect(() => {
    const theme = PersistenceController.retrieve('theme');
    if (theme) {
      changeTheme(theme);
    } else {
      if (window.matchMedia) {
        changeTheme(window.matchMedia('(prefers-color-scheme: dark)').matches ? ApplicationTheme.Dark : ApplicationTheme.Light);
      }
      window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
        changeTheme(e.matches ? ApplicationTheme.Dark : ApplicationTheme.Light);
      });
    }

    // Read persisted values
    UnitController.begin();
    chooseUnit(PersistenceController.retrieve('unitIndex'));
    LanguageController.refreshLanguage();
    Analytics.log('implicit.initialize');
    PwaController.begin(onPWAAvailable, onPWAProcessed);

    // Clean up
    return () =>
      window.matchMedia('(prefers-color-scheme: dark)').removeEventListener('change', (e) => {
        changeTheme(e.matches ? ApplicationTheme.Dark : ApplicationTheme.Light);
      });
  }, []);

  ////////////////////////////////////////////////////////////
  // SERVICE WORKER
  ////////////////////////////////////////////////////////////
  const serviceWorker: any = useServiceWorker();
  const getRegistration = React.useCallback(async () => {
    if ('serviceWorker' in navigator) {
      const promise = Promise.resolve(await navigator.serviceWorker.getRegistration());
      promise
        .then((result) => {
          //console.log('Service Worker registration: ', result);
          if (result && result.waiting) {
            serviceWorker.updateAssets(); // Update the service worker automatically if there is a newer version waiting
          }
        })
        .catch((err) => console.log(err));
    }
  }, [serviceWorker]);

  React.useEffect(() => {
    if (serviceWorker) {
      getRegistration();
    }
  }, [getRegistration, serviceWorker]);

  ///////////////////////////////////////////////////
  // Oauth integration
  ///////////////////////////////////////////////////

  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [searchParams, setSearchParams] = useSearchParams();

  React.useEffect(() => {
    if (!window.location.host.includes("localhost:3000") && !window.location.host.includes("lmtools.vaisala.com") && !window.location.host.includes("lmtools-dev.vaisala.com")) {
      const token = searchParams.get('token') || window.localStorage.getItem('token');
      if (!token) {
        const pkcecodes = pkceChallenge();
        window.localStorage.setItem('pkce', JSON.stringify(pkcecodes.code_verifier))
        window.localStorage.setItem('initUrl', window.location.pathname)
        const hostname = window.location.hostname;

        if (hostname === "localhost") {
          window.location.href = `https://vaisala.my.site.com/myvaisala/services/oauth2/authorize?response_type=code&client_id=${process.env.REACT_APP_SALESFORCE_CLIENT_ID}&redirect_uri=https://localhost:3000/auth&code_challenge=${pkcecodes.code_challenge}&code_challenge_method=S256`
        } else {
          window.location.href = `https://vaisala.my.site.com/myvaisala/services/oauth2/authorize?response_type=code&client_id=${process.env.REACT_APP_SALESFORCE_CLIENT_ID}&redirect_uri=https://${hostname}/auth&code_challenge=${pkcecodes.code_challenge}&code_challenge_method=S256`
        }
      }
    }
  }, [searchParams, navigate]);

  return (
    <>
      <ErrorBoundary FallbackComponent={Fallback}>
        <VaiApplication
          theme={theme}
          className={`vai-application ${activeMethod > -1 ? 'vai-application--method-selected' : ''} ${
            isMobileOnly ? 'vai-application--mobile' : 'vai-application--desktop'
          }`}
        >
          <div
            className={`page-container ${activeMethod > -1 ? 'page-container--method-selected' : ''} ${
              isMobileOnly ? 'page-container--mobile' : 'page-container--desktop'
            }`}
          >
            <Header
              title={'ui.title'}
              unitIndex={unit}
              onChooseUnit={chooseUnit}
              unitOptions={UnitController.modeKeys}
              toggleTheme={toggleTheme}
              onMobileMenuOpen={onMobileMenuOpen}
              onHelpOpen={onHelpOpen}
              showPWAButton={showPWAButton}
              onShowPWAPrompt={onShowPWAPrompt}
              activeMethod={activeMethod}
              theme={theme}
              onMethodSelect={onMethodSelect}
              selectedChemical={selectedChemical}
              onChemicalChanged={setSelectedChemical}
            />
            <LandingPage
              theme={theme}
              themeIndex={theme === ApplicationTheme.Light ? 0 : 1}
              onHelpOpen={onHelpOpen}
              onOptionsOpen={onOptionsOpen}
              unitPresetIndex={unit}
              activeMethod={activeMethod}
              method={method}
              onMethodSelect={onMethodSelect}
              openModal={openModal}
              allChemicalCurves={allChemicalCurves}
              recentChemicals={recentChemicals}
            />
          </div>
          <MobileMenu
            isOpen={mobileMenuOpen}
            unitIndex={unit}
            onChooseUnit={chooseUnit}
            unitOptions={UnitController.modeKeys}
            themeIndex={theme === ApplicationTheme.Light ? 0 : 1}
            onChooseTheme={chooseTheme}
            onHelpOpen={onHelpOpen}
            onClose={onMobileMenuClose}
            showPWAButton={showPWAButton}
            onShowPWAPrompt={onShowPWAPrompt}
          />
        </VaiApplication>
      </ErrorBoundary>
      <Modals {...modal!} theme={theme} activeMethod={activeMethod} />
      <SplashScreen />
    </>
  );
};
